import React, { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { c, msgid } from 'ttag';

import { Button, Modal, ModalContent, ModalFooter, useNotifications } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import {
    AddPartnersToListDocument,
    PartnerListDocument,
    PartnerListsDocument,
} from '../../graphql/__generated__/types';
import EligiblePartnerPicker, { PickedPartner } from './EligiblePartnerPicker';
import { partnerListRefusalOf } from './refusals';

interface Props {
    partnerListId: string;
    onClose: () => void;
}

// Refetch both: the mutation updates the detail, but the list-of-lists document held by
// another screen would otherwise keep a stale « N partenaires » column.
const AddPartnersDialog = ({ partnerListId, onClose }: Props) => {
    const { createNotification } = useNotifications();
    const [picked, setPicked] = useState<PickedPartner[]>([]);

    const [addPartners, { loading, error }] = useMutation(AddPartnersToListDocument, {
        refetchQueries: [PartnerListDocument, PartnerListsDocument],
        awaitRefetchQueries: true,
    });

    const submit = async () => {
        if (picked.length === 0) {
            return;
        }
        const count = picked.length;
        try {
            await addPartners({
                variables: { partnerListId, sourcePartnerIds: picked.map((partner) => partner.sourcePartnerId) },
            });
            createNotification({
                type: 'success',
                text: c('Partner lists').ngettext(
                    msgid`Partner added to the list`,
                    `${count} partners added to the list`,
                    count
                ),
            });
            onClose();
        } catch {
            // Rendered from `error` below; the modal stays open on the selection.
        }
    };

    return (
        <Modal open onClose={onClose} title={c('Partner lists').t`Add partners`} size={760}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <EligiblePartnerPicker
                        excludePartnerListId={partnerListId}
                        picked={picked}
                        onPicked={setPicked}
                        searchPlaceholder={c('Partner lists').t`Search an eligible partner`}
                        emptyTitle={c('Partner lists').t`Every eligible partner is already in this list`}
                        emptyDescription={c('Partner lists')
                            .t`Nothing is left to add. A partner that left the eligible referential is not offered here either: it disappeared from the list on its own.`}
                    />
                    {error && <RefusalNotice refusal={partnerListRefusalOf(error)} />}
                </div>
            </ModalContent>
            <ModalFooter>
                <Button category="weak" onClick={onClose} disabled={loading}>
                    {c('Partner lists').t`Cancel`}
                </Button>
                <Button onClick={submit} disabled={picked.length === 0} loading={loading}>
                    {c('Partner lists').t`Add to the list`}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default AddPartnersDialog;
